import React from 'react';
import { WhisperComponent } from '../../developers/page-components/WhisperComponent';
import { ISitesProps } from '../../../views/merchants/details/MerchantSitesPage';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { MERCHANTS_TABS } from '../../../utils/merchantUtils';
import { BUTTON_TYPE, ButtonComponent } from '../../ButtonComponent';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { SITE_ROUTES } from '../../../constants/developer/site-constants/site-constants';
import { OverflowTextComponent } from '../../OverflowTextComponent';
import { CopyComponent } from '../../CopyComponent';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';
import { LinkComponent } from '../../LinkComponent';

interface Props extends ISitesProps {
	onDelete: (siteId: string) => void;
	onEdit: (siteInfo: ISitesProps) => void;
}

export const SitesTableRow = (props: Props): React.ReactElement => {
	const { onDelete, onEdit, ...itemProps } = props;
	const { id, name, descriptor, customerSupportEmail, customerSupportPhone, customerSupportLink } = itemProps;
	const { t } = useTranslation();
	const { pathname, state } = useLocation();
	const navigate = useNavigate();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);

	const handleEdit = (): void => {
		if (checkRoles(authUser, CLAIMS.MERCHANT_ADMIN)) {
			navigate(`/developers/sites/${id}/${SITE_ROUTES.SITE_INFO}`);
		} else {
			onEdit(itemProps);
		}
	};
	return (
		<tr>
			<td>
				<OverflowTextComponent value={name} additionalBoxClass="w-200px text-truncate fw-bolder"/>

				<div className="w-200px d-flex align-items-center">
					<CopyComponent value={id} title="Site id" additionalClass="me-2"/>
					<WhisperComponent
						whisperText={id}
						mainText={id}
						placement="autoVertical"
						additionalClass="d-block fs-8 text-truncate text-muted"
					/>
				</div>
			</td>
			<td>
				<div className="w-100px fs-6">
					{descriptor}
				</div>
			</td>
			<td>
				<div className="w-150px fs-6">
					{customerSupportEmail}
				</div>
			</td>
			<td>
				<div className="w-150px fs-6">
					{customerSupportPhone}
				</div>
			</td>
			<td className="id-width">
				<WhisperComponent
					whisperText={customerSupportLink}
					mainText={<LinkComponent text={customerSupportLink} link={customerSupportLink} withIcon isUnderLine />}
					placement="top"
					followCursor
					additionalClass="d-block fw-bolder fs-6 text-truncate cursor-pointer w-100"
				/>
			</td>
			{
				checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && (
					<td>
						<div className="d-flex w-250px">
							<ButtonComponent
								buttonType={BUTTON_TYPE.WHITE}
								buttonName="Widget"
								onButtonClick={(): void => navigate(
									`${pathname}/${id}/${MERCHANTS_TABS.WIDGET_CONFIGURATIONS}`,
									{ state: { ...state }}
								)}
								additionalClass="me-3 px-3 py-2 no-letter-spacing"
							/>
							<ButtonComponent
								buttonType={BUTTON_TYPE.WHITE}
								buttonName="Checkout"
								onButtonClick={(): void => navigate(
									`${pathname}/${id}/${MERCHANTS_TABS.CHECKOUT_CONFIGURATIONS}`,
									{ state: { ...state }}
								)}
								additionalClass="me-3 px-3 py-2 no-letter-spacing"
							/>
						<ButtonComponent
							buttonType={BUTTON_TYPE.WHITE}
							buttonName="Sense JS"
							onButtonClick={(): void => navigate(
								`${pathname}/${id}/${MERCHANTS_TABS.SENSE_JS}`,
								{ state: { ...state }}
							)}
							additionalClass="px-3 py-2 no-letter-spacing"
						/>
					</div>
					</td>
				)
			}
			<td>
				<div className="text-muted fw-bolder cursor-pointer w-100px" onClick={handleEdit}>
					{checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) ? 'Edit' : 'View'}
				</div>
			</td>
			{
				checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && (
					<td>
						<div className="text-muted fw-bolder cursor-pointer w-50px" onClick={(): void => onDelete(id)}>
							{t('SITES.ACTION_DELETE')}
						</div>
					</td>
				)
			}
		</tr>
	);
};
